<script setup lang="ts">
import { getFrontendLocale, setFrontendLocale } from '@/composables/useFrontendLocale'
import { usePortalContext } from '@/composables/usePortalContext'
import { reportError } from '@/utils/error-reporting'
import { logout as investorLogout } from '@/routes'
import banker from '@/routes/banker'
import InvestorsUserManagementController from '@controllers/Investors/UserManagementController'
import UserController from '@controllers/User/UserController'
import { Link, router, usePage } from '@inertiajs/vue3'
import axios from 'axios'
import Popover from 'primevue/popover'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const page = usePage()
const { isBankPortal } = usePortalContext()

const user = computed(() => page.props.auth?.user)
const displayName = computed(() => (user.value ? `${user.value.first_name} ${user.value.last_name}` : ''))
const initials = computed(() => `${user.value?.first_name?.[0] ?? ''}${user.value?.last_name?.[0] ?? ''}`.toUpperCase())

const locales = ['de', 'en']
const currentLocale = computed(() => getFrontendLocale())

const panel = ref<InstanceType<typeof Popover> | null>(null)

function togglePanel(event: Event) {
  panel.value?.toggle(event)
}

function selectLanguage(locale: string) {
  const selected = setFrontendLocale(locale)
  const authUser = page.props.auth?.user

  if (authUser?.id) {
    axios
      .put(UserController.update(authUser.id).url, {
        language: selected,
      })
      .catch((error: unknown) => reportError('Persisting the language preference failed', error))
  }

  panel.value?.hide()
}

const logoutRoute = computed(() => (isBankPortal.value ? banker.logout() : investorLogout()))

const showUserManagement = computed(() => !isBankPortal.value)

function logout() {
  router.post(logoutRoute.value.url)
}
</script>

<template>
  <div class="border-surface-200 border-t p-4">
    <button
      type="button"
      class="hover:bg-surface-50 flex w-full items-center gap-3 rounded-lg p-2 text-left transition"
      @click="togglePanel">
      <span
        class="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full"
        :class="user?.profile_image_url ? 'bg-surface-100' : 'text-white'"
        :style="user?.profile_image_url ? undefined : { background: 'var(--gradient-brand)' }">
        <img
          v-if="user?.profile_image_url"
          :key="user.profile_image ?? ''"
          :src="`${user.profile_image_url}?v=${encodeURIComponent(user.profile_image ?? '')}`"
          :alt="displayName"
          class="h-full w-full object-cover" />
        <span v-else class="text-sm font-semibold">{{ initials }}</span>
      </span>
      <span class="min-w-0 flex-1">
        <span class="text-surface-900 block truncate font-medium">{{ displayName }}</span>
        <span class="text-surface-500 block truncate text-sm">{{ user?.email }}</span>
      </span>
      <i class="pi pi-chevron-up text-surface-400 shrink-0" />
    </button>

    <Popover ref="panel">
      <div class="flex w-56 flex-col gap-2">
        <div class="text-surface-500 px-1 text-xs font-medium tracking-wide uppercase">
          {{ t('language') }}
        </div>
        <button
          v-for="locale in locales"
          :key="locale"
          type="button"
          class="hover:bg-surface-50 flex items-center justify-between rounded-md px-3 py-2 text-left transition"
          @click="selectLanguage(locale)">
          <span>{{ t(locale) }}</span>
          <i v-if="currentLocale === locale" class="pi pi-check text-action" />
        </button>

        <template v-if="showUserManagement">
          <Divider class="my-1!" />

          <Link
            :href="InvestorsUserManagementController.index()"
            class="hover:bg-surface-50 flex items-center gap-2 rounded-md px-3 py-2 text-left transition"
            @click="panel?.hide()">
            <i class="pi pi-users text-surface-500" />
            <span>{{ t('user-management') }}</span>
          </Link>
        </template>

        <Divider class="my-1!" />

        <Button
          :label="t('logout')"
          icon="pi pi-power-off"
          severity="secondary"
          text
          class="justify-start!"
          @click="logout" />
      </div>
    </Popover>
  </div>
</template>

<i18n>
{
  "de": {
    "language": "Sprache",
    "de": "Deutsch",
    "en": "Englisch",
    "user-management": "Nutzerverwaltung",
    "logout": "Abmelden"
  },
  "en": {
    "language": "Language",
    "de": "German",
    "en": "English",
    "user-management": "User Management",
    "logout": "Logout"
  }
}
</i18n>
